import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { submit } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from '@/components/ui/field';
import { useStrings, t } from '@/hooks/useStrings';
import type { LocaleStrings, Submission } from '@/types';

type Props = {
	onNext: (submission: Submission) => void;
	onError: () => void;
	initialValues?: Partial<{ name: string; order_number: string; email: string; reason: string }>;
};

const FORM_ID = 'mocy-rev-step1-form';

const buildSchema = (strings: LocaleStrings) =>
	z.object({
		name: z
			.string()
			.trim()
			.min(1, t(strings, 'error_name_required', 'Bitte geben Sie Ihren Namen an.'))
			.max(190),
		order_number: z
			.string()
			.trim()
			.min(1, t(strings, 'error_order_required', 'Bitte geben Sie Ihre Bestellnummer an.'))
			.max(64),
		email: z
			.string()
			.trim()
			.min(1, t(strings, 'error_email_required', 'Bitte geben Sie Ihre E-Mail-Adresse an.'))
			.email(t(strings, 'error_email_invalid', 'Bitte geben Sie eine gültige E-Mail-Adresse an.')),
		reason: z.string().max(2000),
		mocy_website: z.string(),
	});

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

export const Step1 = ({ onNext, onError, initialValues }: Props) => {
	const strings = useStrings();
	const schema = buildSchema(strings);
	const openedAtRef = useRef(Date.now());
	const [formError, setFormError] = useState('');

	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			name: initialValues?.name ?? '',
			order_number: initialValues?.order_number ?? '',
			email: initialValues?.email ?? '',
			reason: initialValues?.reason ?? '',
			mocy_website: '',
		},
	});

	const onSubmit = async (values: FormValues) => {
		setFormError('');
		try {
			const response = await submit({
				name: values.name,
				order_number: values.order_number,
				email: values.email,
				reason: values.reason ?? '',
				mocy_website: values.mocy_website ?? '',
				form_opened_at: openedAtRef.current,
			});

			if (response.ok === true) {
				onNext({
					order_id: response.order_id,
					order_number: response.order_number,
					token: response.token,
					line_items: response.line_items,
					total: response.total,
					currency: response.currency,
					expires_at: response.expires_at,
					name: values.name,
					email: values.email,
					reason: values.reason ?? '',
				});
				return;
			}

			const codeMap: Record<string, string> = {
				invalid_input: t(strings, 'error_invalid_input', 'Bitte prüfen Sie Ihre Angaben: Name, Bestellnummer und E-Mail-Adresse dürfen nicht leer und müssen gültig sein.'),
				rate_limited: t(strings, 'error_rate_limited', 'Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.'),
				deadline_expired: t(strings, 'error_deadline_expired', 'Die Widerrufsfrist für diese Bestellung ist abgelaufen. Eine Widerrufserklärung über dieses Formular ist nicht mehr möglich. Für Rückfragen wenden Sie sich bitte an unseren Kundenservice.'),
				already_revoked: t(strings, 'error_already_revoked', 'Diese Bestellung wurde bereits vollständig widerrufen. Für Rückfragen wenden Sie sich bitte an unseren Kundenservice.'),
				no_open_items: t(strings, 'error_no_open_items', 'Für diese Bestellung stehen keine weiteren Positionen zum Widerruf zur Verfügung.'),
				invalid_or_expired: t(strings, 'error_invalid_or_expired', 'Zu Ihren Angaben konnten wir keine gültige Bestellung finden. Bitte prüfen Sie Bestellnummer und E-Mail-Adresse.'),
			};
			setFormError(
				response.message ||
					codeMap[response.code] ||
					t(strings, 'error_generic', 'Zu Ihren Angaben konnten wir keine gültige Bestellung finden. Bitte prüfen Sie Bestellnummer und E-Mail-Adresse.'),
			);
		} catch (e) {
			console.error('[mocy-revocation] Step1 threw', e);
			onError();
		}
	};

	const submitting = form.formState.isSubmitting;

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t(strings, 'step1_title', 'Vertrag widerrufen')}</CardTitle>
				<CardDescription>
					{t(strings, 'step1_intro', 'Bitte geben Sie Ihre Daten an. Wir prüfen Ihre Bestellung und zeigen Ihnen im nächsten Schritt, welche Positionen Sie widerrufen können.')}
				</CardDescription>
			</CardHeader>

			<CardContent>
				<form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)} noValidate>
					<FieldGroup>
						{formError ? (
							<Alert variant="destructive" aria-live="polite">
								<AlertDescription>{formError}</AlertDescription>
							</Alert>
						) : null}

						<Controller
							name="name"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t(strings, 'label_name', 'Name')}{' '}
										<span aria-hidden="true" className="tw:text-destructive">*</span>
									</FieldLabel>
									<Input
										{...field}
										id={field.name}
										type="text"
										autoComplete="name"
										required
										aria-invalid={fieldState.invalid}
									/>
									{fieldState.invalid && fieldState.error ? (
										<FieldError errors={[fieldState.error]} />
									) : null}
								</Field>
							)}
						/>

						<Controller
							name="order_number"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t(strings, 'label_order', 'Bestellnummer')}{' '}
										<span aria-hidden="true" className="tw:text-destructive">*</span>
									</FieldLabel>
									<Input
										{...field}
										id={field.name}
										type="text"
										inputMode="numeric"
										autoComplete="off"
										required
										aria-invalid={fieldState.invalid}
									/>
									{fieldState.invalid && fieldState.error ? (
										<FieldError errors={[fieldState.error]} />
									) : null}
								</Field>
							)}
						/>

						<Controller
							name="email"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t(strings, 'label_email', 'E-Mail-Adresse')}{' '}
										<span aria-hidden="true" className="tw:text-destructive">*</span>
									</FieldLabel>
									<Input
										{...field}
										id={field.name}
										type="email"
										autoComplete="email"
										required
										aria-invalid={fieldState.invalid}
									/>
									{fieldState.invalid && fieldState.error ? (
										<FieldError errors={[fieldState.error]} />
									) : null}
								</Field>
							)}
						/>

						<Controller
							name="reason"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t(strings, 'label_reason', 'Grund (optional)')}
									</FieldLabel>
									<Textarea
										{...field}
										id={field.name}
										rows={4}
										aria-invalid={fieldState.invalid}
									/>
									<FieldDescription>
										{t(strings, 'hint_reason', 'Sie müssen keinen Grund angeben. Die Angabe ist optional.')}
									</FieldDescription>
									{fieldState.invalid && fieldState.error ? (
										<FieldError errors={[fieldState.error]} />
									) : null}
								</Field>
							)}
						/>

						<Controller
							name="mocy_website"
							control={form.control}
							render={({ field }) => (
								<div className="tw:sr-only" aria-hidden="true">
									<label htmlFor="mocy-rev-hp">
										{t(strings, 'honeypot_label', 'Bitte nicht ausfüllen')}
									</label>
									<input
										{...field}
										id="mocy-rev-hp"
										type="text"
										tabIndex={-1}
										autoComplete="off"
									/>
								</div>
							)}
						/>
					</FieldGroup>
				</form>
			</CardContent>

			<CardFooter>
				<Button
					form={FORM_ID}
					type="submit"
					disabled={submitting}
					aria-busy={submitting}
				>
					{submitting
						? t(strings, 'submitting', 'Wird geprüft…')
						: t(strings, 'submit_step1', 'Vertrag widerrufen')}
				</Button>
			</CardFooter>
		</Card>
	);
};
